/**
 * Surveys v2 API client — mirrors `src/api/v2/survey.py` from
 * orion-backend (PR #261, merged 2026-05-19). Powers the Survey tab of
 * the Regulatory section.
 *
 * Always hits the real backend via `getJsonReal` / `sendJsonReal`; surveys
 * are not part of the mock layer.
 *
 * Endpoints (top-level — NOT scoped under `/submissions/`):
 *   GET    /v2/surveys/             ?submission_id=… &cms_number=… &date_range &paging
 *   POST   /v2/surveys/             body: SurveyCreateWire   → forces survey_source="orion"
 *   PATCH  /v2/surveys/{survey_id}  body: SurveyUpdateWire   → 403 if survey_source="cms"
 *   DELETE /v2/surveys/{survey_id}                            → 403 if survey_source="cms"
 *
 * Aggregation by (facility, year) and facility-name lookup live in
 * `src/api/adapters/regulatory.ts` — this file only speaks wire.
 */
import { getJsonReal, sendJsonReal } from "../client";
import type {
    SurveyCreateWire,
    SurveyGetWire,
    SurveyListResponseWire,
    SurveyUpdateWire,
} from "../../types/survey";

const BASE = "/v2/surveys";

export interface ListSurveysParams {
    submission_id?: string;
    cms_number?: string;
    /** ISO date "YYYY-MM-DD" — inclusive lower bound. */
    survey_date_from?: string;
    /** ISO date "YYYY-MM-DD" — inclusive upper bound. */
    survey_date_to?: string;
    offset?: number;
    limit?: number;
}

export function listSurveys(params: ListSurveysParams = {}): Promise<SurveyListResponseWire> {
    const qs = new URLSearchParams();
    if (params.submission_id) qs.set("submission_id", params.submission_id);
    if (params.cms_number) qs.set("cms_number", params.cms_number);
    if (params.survey_date_from) qs.set("survey_date_from", params.survey_date_from);
    if (params.survey_date_to) qs.set("survey_date_to", params.survey_date_to);
    if (params.offset !== undefined) qs.set("offset", String(params.offset));
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    const suffix = qs.toString();
    return getJsonReal<SurveyListResponseWire>(`${BASE}/${suffix ? `?${suffix}` : ""}`);
}

export function createSurvey(payload: SurveyCreateWire): Promise<SurveyGetWire> {
    return sendJsonReal<SurveyGetWire>(`${BASE}/`, "POST", payload);
}

export function patchSurvey(surveyId: string, payload: SurveyUpdateWire): Promise<SurveyGetWire> {
    return sendJsonReal<SurveyGetWire>(`${BASE}/${encodeURIComponent(surveyId)}`, "PATCH", payload);
}

export function deleteSurvey(surveyId: string): Promise<void> {
    return sendJsonReal<void>(`${BASE}/${encodeURIComponent(surveyId)}`, "DELETE");
}
